import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PendingBookingDto } from '../../api/MeetingsAPI';
import SectionTitle from '../SectionTitle/SectionTitle';
import { parseDateTime } from '../../shared/timeUtils';
import { paymentStatusToTranslationKey, paymentStatusToPillColor } from '../../shared/paymentStatusToTranslationKey';
import { PaymentStatus } from '../../types/payment-statuses';
import Pill from '../Pill/Pill';
import { BolderText } from '../BolderText/BolderText';
import TinyText from '../TinyText/TinyText';
import CopyText from '../CopyText/CopyText';
import { Settings } from '../../store/reducers/AccountSlice';
import PaymentStatusToggle from '../PaymentStatusToggle/PaymentStatusToggle';

type Props = {
  booking: PendingBookingDto | null;
  isLoading?: boolean;
  onPaymentStatusChange?: (id: number, paid: boolean) => Promise<void>;
};

function PendingBookingDetails({ booking, isLoading = false, onPaymentStatusChange }: Props) {
  const { t } = useTranslation();
  const settings: Settings = useSelector((state: any) => state.settings.value);

  const copyTrackingLink = () => {
    if (!booking?.reservationReference || !settings.bookingWidgetId) return;
    return `${window.location.origin}/bookings/${settings.bookingWidgetId}/${booking.reservationReference}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded w-full"></div>
            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center py-8">
        <TinyText text={t("bookings.details.not_found")} />
      </div>
    );
  }

  const appointmentDateTime = parseDateTime(`${booking.date}T${booking.time}`);

  return (
    <div className="space-y-6">
      {/* Tracking Link - Top Left */}
      {booking?.reservationReference && (
        <div className="flex items-center mb-2 space-x-2">
          {copyTrackingLink() && <CopyText text={copyTrackingLink()!} />}
          <span className="text-sm opacity-80">
            #{booking.reservationReference}
          </span>
        </div>
      )}

      {/* Booking Overview */}
      <div>
        <SectionTitle text={t("bookings.details.overview")} />
        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-600">{t("bookings.details.customer")}:</span>
            <div className="text-right">
              <BolderText text={booking.customerName} />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-600">{t("bookings.details.meeting_type")}:</span>
            <div className="text-right">
              <BolderText text={booking.meetingType} />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-600">{t("bookings.details.appointment")}:</span>
            <div className="text-right">
              <BolderText text={appointmentDateTime.date} />
              <div className="text-sm text-gray-600">{appointmentDateTime.time}</div>
            </div>
          </div>
        </div>
      </div>

      {booking.requiresPayment && (
        <div>
          <SectionTitle text={t("bookings.details.payment")} />
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600">{t("bookings.details.payment_status")}:</span>
              <div className="flex items-center gap-2">
                <Pill 
                  text={t(paymentStatusToTranslationKey(booking.paid ? PaymentStatus.PAID : PaymentStatus.PENDING))} 
                  color={paymentStatusToPillColor(booking.paid ? PaymentStatus.PAID : PaymentStatus.PENDING)} 
                />
                {onPaymentStatusChange && (
                  <PaymentStatusToggle
                    id={booking.id}
                    paid={booking.paid}
                    onPaymentStatusChange={(id, paid) => onPaymentStatusChange(Number(id), paid)}
                    size="sm"
                  />
                )}
              </div>
            </div>
            {booking.refundable && (
              <>
                {booking.refundAmount && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">{t("bookings.details.refund_amount")}:</span>
                    <BolderText text={booking.refundAmount} />
                  </div>
                )}
                {booking.refundStatus && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">{t("bookings.details.refund_status")}:</span>
                    <Pill 
                      text={t(paymentStatusToTranslationKey(booking.refundStatus as any))} 
                      color={paymentStatusToPillColor(booking.refundStatus as any)} 
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}


    </div>
  );
}

export default PendingBookingDetails;
